import { useState, useEffect } from "react";
import { BriefClient, IndexedDBStorage } from "@gbougakov/brief-ts";
import { BriefProvider } from "@gbougakov/brief-ts/react";
import { AuthScreen } from "./components/auth/AuthScreen";
import { NoteStream } from "./components/notes/NoteStream";
import { useAuthState, AuthContext } from "./hooks/use-auth";
import { BRIEF_ENDPOINT } from "./lib/constants";
import "./index.css";

function AuthenticatedApp({
  sessionToken,
  masterKey,
}: {
  sessionToken: string;
  masterKey: CryptoKey;
}) {
  const [client, setClient] = useState<BriefClient | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let destroyed = false;
    let instance: BriefClient | null = null;

    BriefClient.create({
      storage: new IndexedDBStorage(),
      sessionToken,
      masterKey,
      endpoint: BRIEF_ENDPOINT,
    })
      .then((c) => {
        if (destroyed) {
          c.destroy();
          return;
        }
        instance = c;
        c.connect();
        setClient(c);
      })
      .catch((e) => {
        if (!destroyed) setError(e.message);
      });

    return () => {
      destroyed = true;
      instance?.destroy();
    };
  }, [sessionToken, masterKey]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-8">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to initialize</p>
          <p className="text-sm text-outline">{error}</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <BriefProvider client={client}>
      <NoteStream />
    </BriefProvider>
  );
}

export function App() {
  const auth = useAuthState();

  return (
    <AuthContext.Provider value={auth}>
      {auth.state === "authenticated" &&
      auth.sessionToken &&
      auth.masterKey ? (
        <AuthenticatedApp
          sessionToken={auth.sessionToken}
          masterKey={auth.masterKey}
        />
      ) : auth.state === "loading" ? (
        <div className="flex items-center justify-center min-h-screen">
          <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <AuthScreen />
      )}
    </AuthContext.Provider>
  );
}

export default App;
